import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TechnologicalTransferDomainSelectControl } from '@/module/Author/TechnologicalTransfer/components/form/TechnologicalTransferDomainSelectControl';
import { TechnologicalTransferPartnerTypeSelectControl } from '@/module/Author/TechnologicalTransfer/components/form/TechnologicalTransferPartnerTypeSelectControl';
import { TechnologicalTransferResultSelectControl } from '@/module/Author/TechnologicalTransfer/components/form/TechnologicalTransferResultSelectControl';
import { TechnologicalTransferTypeSelectControl } from '@/module/Author/TechnologicalTransfer/components/form/TechnologicalTransferTypeSelectControl';
import { TechnologicalTransferMembershipSelectControl } from '@/module/Nomen/TechnologicalTransferMembership/TechnologicalTransferMembershipSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, PublishedAtControl, TextInputControl } from '@/core/components';

import { TechnologicalTransferSchema } from './model/TechnologicalTransferSchema';

interface TechnologicalTransferFormProps extends FormSubmit<TechnologicalTransferSchema> {
  title: string;
}

export function TechnologicalTransferForm(props: TechnologicalTransferFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<TechnologicalTransferSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<TechnologicalTransferSchema>
              withAsterisk
              name="title"
              label={t('technologicalTransfers.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Group>
              <TextInputControl<TechnologicalTransferSchema>
                withAsterisk
                name="registrationNo"
                label={t('technologicalTransfers.form.fields.registrationNo')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl<TechnologicalTransferSchema>
                name="contractNo"
                label={t('technologicalTransfers.form.fields.contractNo')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <Group>
              <TextInputControl<TechnologicalTransferSchema>
                withAsterisk
                name="partnershipAgreement"
                label={t('technologicalTransfers.form.fields.partnershipAgreement')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl<TechnologicalTransferSchema>
                withAsterisk
                name="protocol"
                label={t('technologicalTransfers.form.fields.protocol')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <TechnologicalTransferTypeSelectControl
              isSubmitting={isSubmitting}
              watch={form.watch}
            />
            <TechnologicalTransferPartnerTypeSelectControl
              isSubmitting={isSubmitting}
              watch={form.watch}
            />
            <TechnologicalTransferDomainSelectControl
              isSubmitting={isSubmitting}
              watch={form.watch}
            />
            <TechnologicalTransferResultSelectControl
              isSubmitting={isSubmitting}
              watch={form.watch}
            />
          </Content>
          <Content type="full">
            <Group>
              <PublishedAtControl<TechnologicalTransferSchema>
                name="startDate"
                label={t('technologicalTransfers.form.fields.startDate')}
                isSubmitting={isSubmitting}
              />
              <PublishedAtControl<TechnologicalTransferSchema>
                name="endDate"
                label={t('technologicalTransfers.form.fields.endDate')}
                isSubmitting={isSubmitting}
              />
            </Group>
          </Content>
          <Content type="full">
            <AuthorListControl<TechnologicalTransferSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <TechnologicalTransferMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
